/**
 * IflowDiagram - koordinaten-basierter iFlow-Renderer (1:1 wie die SAP Web GUI).
 * Nutzt die im BPMN gespeicherten BPMNShape/BPMNEdge-Koordinaten (gleiche Technik wie
 * die Schnittstellen-Content-Visualisierung), gerendert im iio/aib-Design.
 * Parser/Icons/Kategorien kommen aus utils/bpmnShared (EINE Quelle fuer alle Renderer).
 *
 * Optionale Props (ohne sie rendert die Komponente exakt wie im KI iFlow Builder):
 * - selectedId: hebt ein Element dauerhaft hervor (Detail-Panels).
 * - marks: { [elementId]: "add" | "del" | "chg" } - Diff-Dekoration ohne Glow:
 *   2px-Rahmen + Zeichen (+/-/Delta) + Label-Praefix aus dem Katalog,
 *   Entferntes gestrichelt. Farbe traegt nie allein.
 * - onlyChanges: dimmt unmarkierte Elemente ("Nur Aenderungen"-Filter).
 * - focus: "add" | "del" | "chg" | null - hebt NUR die Elemente dieser Diff-Rubrik
 *   hervor und dimmt alles andere (Klick auf die Legende im Compare-Dialog).
 */
import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { parseBpmnElements, parseBpmnLayout, clipEdge as clip, iconFor, TYPE_LABEL, categoryOf, layoutNodeLabel } from "../utils/bpmnShared";

// badge ist ein Zeichen und bleibt sprachlos; prefixKey zeigt auf den Katalog.
const MARK = {
  add: { color: "var(--iio-dia-diff-add)", badge: "+", prefixKey: "neu" },
  del: { color: "var(--iio-dia-diff-del)", badge: "−", prefixKey: "weg" },
  chg: { color: "var(--iio-dia-diff-chg)", badge: "Δ", prefixKey: "geaendert" },
};

const MIN_SCALE = 0.2, MAX_SCALE = 4;
const ZBTN = { background: "none", border: 0, cursor: "pointer", color: "var(--iio-text-2)", fontSize: 14, fontWeight: 600, height: 20, width: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", borderRadius: 4 };

export default function IflowDiagram({ bpmnXml, onElementClick, height, selectedId, marks, onlyChanges, focus }) {
  const { t } = useTranslation("gemeinsam");
  const markerId = useId().replace(/:/g, "");
  const [hoveredId, setHoveredId] = useState(null);
  const [vt, setVt] = useState({ x: 0, y: 0, scale: 1 });
  const [panning, setPanning] = useState(false);
  const drag = useRef({ on: false });
  const svgRef = useRef(null);
  const layout = useMemo(() => parseBpmnLayout(bpmnXml), [bpmnXml]);
  const elements = useMemo(() => parseBpmnElements(bpmnXml), [bpmnXml]);
  const { shapes, edges, bbox } = layout;

  // Die viewBox spannt die ganze Zeichnung auf die Bildflaeche - dieser Einpass-
  // Faktor muss bekannt sein, sonst luegt die Prozentanzeige (gemessen: "100%"
  // bei tatsaechlich 29%) und der Detailgrad kann nicht mitwandern.
  const vbW = bbox.w + 100, vbH = bbox.h + 100;
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = svgRef.current; if (!el) return;
    const read = () => { const r = el.getBoundingClientRect(); setBox({ w: r.width, h: r.height }); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fitScale = box.w && box.h ? Math.min(box.w / vbW, box.h / vbH) : 1;
  const eff = fitScale * vt.scale;            // echte Skalierung auf dem Bildschirm
  const zoomLevel = Math.round(eff * 100);

  // Bildschirmpunkt -> viewBox-Koordinaten (beruecksichtigt preserveAspectRatio automatisch)
  const toUser = (clientX, clientY) => {
    const svg = svgRef.current; if (!svg || !svg.getScreenCTM) return null;
    const m = svg.getScreenCTM(); if (!m) return null;
    const pt = svg.createSVGPoint(); pt.x = clientX; pt.y = clientY;
    return pt.matrixTransform(m.inverse());
  };
  // 1:1 muss auch bei grossen Flows erreichbar bleiben (Einpassen liegt dort bei 0.15)
  const maxScale = Math.max(MAX_SCALE, fitScale ? 1.2 / fitScale : MAX_SCALE);
  const clampScale = s => Math.max(MIN_SCALE, Math.min(maxScale, s));
  // Zoom zum Cursor: der Punkt unter dem Zeiger bleibt stehen
  const zoomAt = (next, clientX, clientY) => {
    const u = clientX == null ? null : toUser(clientX, clientY);
    setVt(p => {
      const s = clampScale(next(p.scale));
      if (s === p.scale) return p;
      if (!u) return { ...p, scale: s };
      const k = s / p.scale;
      return { x: u.x - (u.x - p.x) * k, y: u.y - (u.y - p.y) * k, scale: s };
    });
  };
  const zoom = d => zoomAt(s => s * (d > 0 ? 1.25 : 1 / 1.25), null, null);
  const fit = () => setVt({ x: 0, y: 0, scale: 1 });
  const oneToOne = () => setVt({ x: 0, y: 0, scale: fitScale ? 1 / fitScale : 1 });

  // Wheel bewusst per addEventListener mit passive:false - React registriert
  // onWheel passiv, dort laeuft preventDefault() ins Leere und die Seite scrollt mit.
  // Gestik: Scrollen/Pinch zoomt (zum Cursor), Ziehen verschiebt.
  // Schrittweite proportional zu deltaY und pro Ereignis geklemmt: ein Mausrad-
  // Rasten (~100) gibt ~11%, die vielen kleinen Trackpad-Deltas je ~1.5% - damit
  // rast die Geste nicht (vorher: 10 Trackpad-Ereignisse = 100% -> 20%).
  useEffect(() => {
    const el = svgRef.current; if (!el) return;
    const h = (e) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1;
      const dy = (e.deltaY || 0) * unit;
      if (!dy) return;
      const f = Math.min(1.25, Math.max(0.8, Math.exp(-dy * (Math.abs(dy) < 40 ? 0.0075 : 0.00113))));
      zoomAt(s => s * f, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, [fitScale, vt.scale]);
  const onDown = useCallback((e) => {
    if (e.button !== 0) return;
    window.getSelection && window.getSelection().removeAllRanges();
    drag.current = { on: true, sx: e.clientX, sy: e.clientY, tx: vt.x, ty: vt.y, moved: false };
    setPanning(true);
    const svg = svgRef.current;
    const perPx = (() => { const m = svg && svg.getScreenCTM && svg.getScreenCTM(); return m ? 1 / (m.a || 1) : 1; })();
    const mv = ev => {
      if (!drag.current.on) return;
      const dx = ev.clientX - drag.current.sx, dy = ev.clientY - drag.current.sy;
      if (!drag.current.moved && Math.abs(dx) + Math.abs(dy) < 3) return;
      drag.current.moved = true;
      setVt(p => ({ ...p, x: drag.current.tx + dx * perPx, y: drag.current.ty + dy * perPx }));
    };
    const up = () => { drag.current.on = false; setPanning(false); window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  }, [vt.x, vt.y]);

  const isGateway = t => t && /gateway/i.test(t);
  const isEvent = t => t && /event/i.test(t);
  const poolIds = [], subIds = [], normalIds = [];
  for (const id of Object.keys(shapes)) { const t = elements[id]?.type || ""; if (t === "participant") poolIds.push(id); else if (t === "subProcess") subIds.push(id); else normalIds.push(id); }

  // Nur ein echter Klick oeffnet den Step. Bei ~60 Knoten greift man beim
  // Verschieben fast immer auf eine Karte - ohne Schwelle oeffnet jedes Pannen
  // das Detail-Panel.
  const click = (id, e) => {
    e.stopPropagation();
    if (drag.current.moved) return;
    const info = elements[id] || {};
    onElementClick && onElementClick({ id, name: info.name || id, type: info.type, activityType: info.activityType, properties: info.properties || {} });
  };

  // Diff-Dekoration: SVG-Rahmen + Zeichen um ein Shape (auch fuer Events/Gateways)
  const mk = (id) => (marks && MARK[marks[id]]) || null;
  // Pools/Subprozesse bleiben als Strukturkontext immer voll sichtbar.
  // focus (Legenden-Klick) dimmt ALLES ausser der gewaehlten Rubrik - auch
  // Markierungen anderer Rubriken, sonst traegt der Filter nichts.
  const dimmed = (id) => {
    const ty = elements[id]?.type;
    if (ty === "participant" || ty === "subProcess") return false;
    if (focus) return (marks && marks[id]) !== focus;
    if (!onlyChanges || !marks || marks[id]) return false;
    return true;
  };
  // 0.14 statt 0.28: bei 0.28 blieb der Rest so praesent, dass die Aenderungen
  // im "Nur Aenderungen"-Modus kaum herausstachen (Davids Befund 26.08.).
  const DIM = 0.14;
  const svgMark = (m, sh) => !m ? null : <g pointerEvents="none">
    <rect x={sh.x - 4} y={sh.y - 4} width={sh.w + 8} height={sh.h + 8} rx={8} fill={m.color} fillOpacity={0.09} stroke={m.color} strokeWidth={2} strokeDasharray={m.badge === "−" ? "5,4" : undefined} vectorEffect="non-scaling-stroke" />
    <text x={sh.x + sh.w + 4} y={sh.y - 4} textAnchor="middle" dominantBaseline="middle" fontSize={14} fontWeight={700} fill={m.color} stroke="var(--iio-dia-canvas)" strokeWidth={3} paintOrder="stroke" fontFamily="Source Sans 3,sans-serif">{m.badge}</text>
  </g>;

  const renderShape = (id) => {
    const sh = shapes[id]; if (!sh) return null;
    const info = elements[id] || {}; const typ = info.type || "task"; const name = info.name || ""; const hov = hoveredId === id || selectedId === id;
    const m = mk(id);
    // Diff-Rahmen behalten ihren Kontrast; Filter dimmen Rahmen und Inhalt zusammen.
    const wrapG = (inner) => <g key={id} data-diff={marks?.[id]} opacity={dimmed(id) ? DIM : 1}>
      {m && <title>{t(`bauteil.diff.${m.prefixKey}`)}: {name || id}</title>}
      {inner}{svgMark(m, sh)}</g>;

    if (isGateway(typ)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2, g = r * 0.4;
      // Symbol (X/+) traegt die Signalfarbe, der RAND bleibt neutral: ein orange
      // umrandeter Router las sich im Diff wie eine GEAEND.-Markierung.
      const sym = typ === "parallelGateway"
        ? <g stroke="var(--iio-signal)" strokeWidth={2} strokeLinecap="round"><line x1={cx} y1={cy - g} x2={cx} y2={cy + g} /><line x1={cx - g} y1={cy} x2={cx + g} y2={cy} /></g>
        : <g stroke="var(--iio-signal)" strokeWidth={2} strokeLinecap="round"><line x1={cx - g * .7} y1={cy - g * .7} x2={cx + g * .7} y2={cy + g * .7} /><line x1={cx + g * .7} y1={cy - g * .7} x2={cx - g * .7} y2={cy + g * .7} /></g>;
      return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
        <polygon points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} fill="var(--iio-dia-node)" stroke={hov ? "var(--iio-brand-fg)" : "var(--iio-dia-node-border)"} strokeWidth={hov ? 2 : 1.3} vectorEffect="non-scaling-stroke" />{sym}
      </g>);
    }
    if (isEvent(typ)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2;
      const cmd = (info.properties?.cmdVariantUri || "").toLowerCase(), nm = name.toLowerCase();
      const isErr = cmd.includes("error") || nm.includes("error"), isTmr = cmd.includes("timer") || nm.includes("timer");
      const isEnd = typ === "endEvent";
      // End-Events gedaempft (--iio-dia-end) und mit normaler Strichstaerke: das
      // kraeftige Critical-Rot mit 2.6px las sich im Diff wie eine Loeschung.
      const col = isErr ? "var(--iio-critical)" : isEnd ? "var(--iio-dia-end)" : "var(--iio-success)";
      return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
        <circle cx={cx} cy={cy} r={r} fill="var(--iio-dia-node)" stroke={col} strokeWidth={hov ? 2.2 : 1.6} vectorEffect="non-scaling-stroke" />
        <g stroke={col} fill="none" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round" transform={`translate(${cx - r * .5},${cy - r * .5}) scale(${r / 24})`}>
          <g dangerouslySetInnerHTML={{ __html: isTmr ? '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>' : isErr ? '<path d="M10 4 2 19h20L14 4z"/><path d="M12 10v4"/>' : '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>' }} />
        </g>
        {name && <text x={cx} y={cy + r + 11} textAnchor="middle" fontSize={9} fill="var(--iio-dia-label)" fontFamily="Source Sans 3,sans-serif">{name.length > 22 ? name.slice(0, 20) + "…" : name}</text>}
      </g>);
    }
    if (typ === "participant") {
      const iflType = info.properties?.["ifl:type"] || "";
      const isProc = name === "Integration Process" || iflType === "IntegrationProcess";
      const isRecv = iflType === "EndpointRecevier" || iflType === "EndpointReceiver";
      // Solide Pool-Flaeche wie .aib-pool im KI-Builder: erst dadurch liegen die
      // Karten auf einer Ebene und nicht direkt auf dem fast schwarzen Canvas.
      const col = isRecv && !marks ? "var(--iio-signal)" : "var(--iio-dia-pool-border)";
      const lblCol = isRecv && !marks ? "var(--iio-signal)" : "var(--iio-dia-pool-label)";
      return wrapG(<g>
        <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} fill="var(--iio-dia-pool)" stroke={col} strokeWidth={1.4} strokeDasharray={isProc ? undefined : "6,4"} rx={6} vectorEffect="non-scaling-stroke" />
        {name && <text x={sh.x + 13} y={sh.y + sh.h / 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={700} letterSpacing="0.08em" fill={lblCol} fontFamily="Source Sans 3,sans-serif" transform={`rotate(-90 ${sh.x + 13} ${sh.y + sh.h / 2})`}>{(name.length > 28 ? name.slice(0, 26) + "…" : name).toUpperCase()}</text>}
      </g>);
    }
    if (typ === "subProcess") {
      // Neutral gestrichelt statt Critical-Rot: der rot-gestrichelte Rahmen war
      // von der WEG-Markierung des Diffs (rot + gestrichelt) nicht unterscheidbar.
      return wrapG(<g>
        <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} rx={8} fill="transparent" stroke="var(--iio-dia-pool-border)" strokeWidth={1.2} strokeDasharray="6,3" vectorEffect="non-scaling-stroke" />
        {name && <text x={sh.x + 10} y={sh.y + 15} fontSize={10} fontWeight={700} fill="var(--iio-dia-pool-label)" fontFamily="Source Sans 3,sans-serif">{name}</text>}
      </g>);
    }
    // Task / callActivity / serviceTask -> aib-Karte (gleiches Design wie vorher, an BPMN-Koordinaten)
    // + 3px-Kategorien-Akzent links (Transform/Route/Call/Persist), Bedeutung dreifach kodiert
    const at = info.activityType || "";
    const cat = categoryOf(info);
    const typeLbl = TYPE_LABEL[at] || at || typ;
    // SAP-Shapes sind meist nur 100x60px - dafuer ein kompaktes Innenlayout
    // (Icon + Typ in der Kopfzeile, Name darunter ueber die volle Breite).
    // Das Builder-Layout (Icon neben dem Namen) braucht >=130x76px.
    // Karte vollstaendig als SVG (kein foreignObject): skalierte HTML-Inhalte
    // muessen von Chrome pro Frame neu gerastert werden - bei 28 Karten und
    // Zoom 3.5 fror das Pannen den Tab ein. Beschriftungs-Layout kommt aus
    // bpmnShared, damit Doku-Renderer und Canvas dieselbe Quelle nutzen.
    const L = layoutNodeLabel(name, typeLbl, sh, { eff, minPx: 7 });
    const accent = cat ? cat.color : "var(--iio-dia-label)";
    return wrapG(<g onMouseEnter={() => setHoveredId(id)} onMouseLeave={() => setHoveredId(null)} onClick={e => click(id, e)} style={{ cursor: "pointer" }}>
      <title>{name ? (typeLbl ? `${name} · ${typeLbl}` : name) : typeLbl}</title>
      <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h} rx={6} fill="var(--iio-dia-node)"
        stroke={hov ? "var(--iio-brand-fg)" : "var(--iio-dia-node-border)"} strokeWidth={hov ? 2 : 1} vectorEffect="non-scaling-stroke" />
      {cat && <line x1={sh.x + 2.5} y1={sh.y + 7} x2={sh.x + 2.5} y2={sh.y + sh.h - 7} stroke={cat.color} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
      {L.showIcon && <g stroke={accent} fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"
        transform={`translate(${sh.x + 8},${sh.y + 7}) scale(0.46)`} dangerouslySetInnerHTML={{ __html: iconFor(at) }} />}
      {/* Beschriftung IMMER zeichnen, auch stark herausgezoomt: leere Kaesten lesen
          sich wie ein Defekt. Bei kleiner Skalierung wird die Schrift gegen den
          Zoom angehoben, aber nur so weit, wie sie in die Box passt (siehe
          layoutNodeLabel: mehr Zeilen -> kleinere Schrift, max. 3 Zeilen). */}
      {name && L.lines.map((ln, i) => (
        <text key={i} x={L.cx} y={L.y0 + i * L.lh} textAnchor="middle" dominantBaseline="middle"
          fontSize={L.fs} fontWeight={600} fill="var(--iio-text-1)" fontFamily="Source Sans 3,sans-serif">{ln}</text>
      ))}
      <text x={L.cx} y={L.typeY} textAnchor="middle" dominantBaseline="middle" fontSize={L.tfs} fontWeight={600}
        letterSpacing={L.tls} fill="var(--iio-dia-label)" fontFamily="Source Sans 3,sans-serif">
        {m && sh.w >= 140 ? `${t(`bauteil.diff.${m.prefixKey}`)} · ${L.typeText}` : L.typeText}</text>
    </g>);
  };

  return (
    <div style={{
      position: "relative", width: "100%", height: height || "100%", overflow: "hidden",
      background: "var(--iio-dia-canvas)",
      backgroundImage: "linear-gradient(to right, var(--iio-dia-grid) 1px, transparent 1px), linear-gradient(to bottom, var(--iio-dia-grid) 1px, transparent 1px)",
      backgroundSize: "24px 24px",
    }}>
      {/* Karten-Styles lokal mitliefern, damit das Diagramm auf JEDER Seite
          (Content, Audit, Compare, Builder) ohne Seiten-CSS funktioniert.
          Farben aus den --iio-dia-*-Tokens: die bg-Stufen allein trennen einen
          dichten Graph nicht (bg-2 auf bg-0 sind im Dark nur 1.26:1). */}
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10, display: "flex", alignItems: "center", gap: 2, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: "3px 5px" }}>
        <button onClick={() => zoom(-1)} title={t("bauteil.diagramm.verkleinern")} aria-label={t("bauteil.diagramm.verkleinern")} style={ZBTN}>−</button>
        {/* Anzeige ist die ECHTE Skalierung (Einpassen mal Zoom), nicht relativ zum Einpassen */}
        <span title={t("bauteil.diagramm.zoomTitel")} style={{ fontSize: 10.5, color: "var(--iio-text-1)", minWidth: 36, textAlign: "center", fontWeight: 600 }}>{zoomLevel} %</span>
        <button onClick={() => zoom(1)} title={t("bauteil.diagramm.vergroessern")} aria-label={t("bauteil.diagramm.vergroessern")} style={ZBTN}>+</button>
        <span style={{ width: 1, height: 16, background: "var(--iio-border)", margin: "0 3px" }} />
        {/* Breite bleibt auto: "Einpassen" ist laenger als "Fit" */}
        <button onClick={fit} title={t("bauteil.diagramm.einpassen")} aria-label={t("bauteil.diagramm.einpassen")} style={{ ...ZBTN, fontSize: 11, fontWeight: 600, width: "auto", padding: "0 6px" }}>{t("bauteil.diagramm.einpassen")}</button>
        <button onClick={oneToOne} title={t("bauteil.diagramm.originalgroesse")} aria-label={t("bauteil.diagramm.originalgroesse")} style={{ ...ZBTN, fontSize: 11, fontWeight: 600, width: "auto", padding: "0 6px" }}>1:1</button>
      </div>
      <svg ref={svgRef} width="100%" height="100%" style={{ display: "block", cursor: panning ? "grabbing" : "grab", userSelect: "none", WebkitUserSelect: "none" }}
        viewBox={`${bbox.minX - 50} ${bbox.minY - 50} ${bbox.w + 100} ${bbox.h + 100}`} onMouseDown={onDown} onDragStart={e => e.preventDefault()}>
        <defs>
          {[['normal', 'var(--iio-dia-edge)'], ['message', marks ? 'var(--iio-dia-edge)' : 'var(--iio-signal)'], ...Object.entries(MARK).map(([key, value]) => [key, value.color])].map(([key, color]) =>
            <marker key={key} id={`${markerId}-${key}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth={7} markerHeight={7} orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill={color} /></marker>)}
        </defs>
        <g transform={`translate(${vt.x},${vt.y}) scale(${vt.scale})`}>
          {poolIds.map(renderShape)}
          {subIds.map(renderShape)}
          {edges.map(e => {
            const pts = clip(e, shapes, elements).map(w => `${w.x},${w.y}`).join(" ");
            const em = mk(e.id);
            return <polyline key={e.id} data-diff={marks?.[e.id]} points={pts} fill="none" opacity={dimmed(e.id) ? DIM : 1} stroke={em ? em.color : e.isMessageFlow && !marks ? "var(--iio-signal)" : "var(--iio-dia-edge)"} strokeWidth={em ? 2 : e.isMessageFlow ? 1.1 : 1.4} vectorEffect="non-scaling-stroke" strokeDasharray={e.isMessageFlow || (em && em.badge === "−") ? "5,4" : undefined} markerEnd={`url(#${markerId}-${em ? marks[e.id] : e.isMessageFlow ? 'message' : 'normal'})`}>
              {em && <title>{t(`bauteil.diff.${em.prefixKey}`)}: {elements[e.id]?.name || e.id}</title>}
            </polyline>;
          })}
          {normalIds.map(renderShape)}
        </g>
      </svg>
    </div>
  );
}
